// Lógica pura de «Cuentas por contrato»: el borrador, los cambios contra lo guardado, la lista legible de cambios,
// los pares MT → CT, lo que ve el cliente y «Preparar el corte». Sin Angular ni HTTP: se prueba sola.
//
// `datos` es lo que devuelve GET /api/perfil-bancario/cuentas-contrato (lo guardado). El borrador guarda SOLO lo que
// el usuario cambió; `aplicarBorrador(datos, borrador)` devuelve esos mismos datos con los cambios puestos, y con eso
// se calculan los bloqueos, el teléfono y los conteos.

import { METODOS_PAGO_MOVIL_Y_TRANSFERENCIA, avisar, esVisible, perfilDe, revisar } from './cuentas-contrato-reglas';

/** Datos de una cuenta que se pueden editar en el formulario (clonar o editar una CT), con los nombres del núcleo. */
export interface DatosCuenta {
  number?: string | null; phone?: string | null; bankCode?: string | null; taxId?: string | null; identity?: string | null;
  name?: string | null; description?: string | null; subscriptor_simbolo?: string | null; subscriptor_cuenta?: string | null; subscriptor_mp?: string | null;
}
export interface CambiosCuenta extends DatosCuenta { showClient?: boolean; showChatbot?: boolean; status?: boolean; etiquetas?: string[]; }
/** Cuenta CT nueva del borrador: clon de una MT. `datos` es el formulario completo; al guardar solo se manda lo que difiere de la MT. */
export interface CuentaNueva { tmp_id: string; clon_de: string; datos: DatosCuenta; }
export interface CambiosOpcion { showClient?: boolean; priority?: number; }
export interface Borrador {
  opciones: { [id: string]: CambiosOpcion };
  cuentas: { [id: string]: CambiosCuenta };
  /** Cuentas CT nuevas (clones de una MT). Nacen apagadas y ocultas. */
  nuevas: CuentaNueva[];
  /** Contrato que cobra en la simulación (solo «Preparar el corte»). null = el real. */
  perfilSimulado: string | null;
}

export const CAMPOS_OPCION: (keyof CambiosOpcion)[] = ['showClient', 'priority'];
/** Campos de datos del formulario, en el orden en que se muestran. */
export const CAMPOS_DATOS: (keyof DatosCuenta)[] = [
  'number', 'phone', 'bankCode', 'taxId', 'identity', 'name', 'description', 'subscriptor_simbolo', 'subscriptor_cuenta', 'subscriptor_mp'
];
export const CAMPOS_CUENTA: (keyof CambiosCuenta)[] = ['showClient', 'showChatbot', 'status', 'etiquetas', ...CAMPOS_DATOS];

/** Nombre legible de cada campo de datos (lista de cambios y bitácora). `bank` y `bankCode` se dicen como «Banco». */
export const ETIQUETA_CAMPO: { [campo: string]: string } = {
  number: 'Número de cuenta', phone: 'Teléfono', bankCode: 'Banco', taxId: 'RIF de la empresa receptora', identity: 'Cédula o RIF del titular',
  name: 'Nombre', description: 'Descripción', subscriptor_simbolo: 'SAP · símbolo', subscriptor_cuenta: 'SAP · cuenta', subscriptor_mp: 'SAP · MP'
};

/** Minúscula inicial para meter el rótulo en una frase sin tocar siglas (RIF). */
export const enFrase = (t: string): string => t.charAt(0).toLowerCase() + t.slice(1);

export const borradorVacio = (): Borrador => ({ opciones: {}, cuentas: {}, nuevas: [], perfilSimulado: null });

/** Igualdad para comparar con lo guardado: sin dato, null y texto vacío son lo mismo. */
const igual = (a: any, b: any): boolean => {
  const n = (v: any) => (v === undefined || v === '' ? null : v);
  return JSON.stringify(n(a)) === JSON.stringify(n(b));
};
const copiaBorrador = (b: Borrador): Borrador => ({ opciones: { ...b.opciones }, cuentas: { ...b.cuentas }, nuevas: [...(b.nuevas || [])], perfilSimulado: b.perfilSimulado });
const idTxt = (v: any): string => String(v && v._id !== undefined ? v._id : v);

/** Nombre del contrato para decirlo en palabras. */
export const nombreContrato = (perfil: string | null | undefined): string => {
  const p = String(perfil || '').toUpperCase();
  if (p === 'MT') return 'Mundo Total';
  if (p === 'CT') return 'CrediTotal';
  return p || 'el contrato';
};

// ------------------------------------------------------------------------------------------------
// Acceso a los datos
// ------------------------------------------------------------------------------------------------

/** Cuentas de las opciones (MT y CT), en el orden que manda el núcleo. */
export const cuentasDeOpcion = (opcion: any): any[] => [...(opcion?.cuentas?.mt || []), ...(opcion?.cuentas?.ct || [])];

/** Todas las cuentas conocidas: las de las opciones y las de los cobros fijos, sin repetir. */
export const todasLasCuentas = (datos: any): any[] => {
  const vistas = new Set<string>();
  const lista: any[] = [];
  const agregar = (c: any) => { if (c && !vistas.has(idTxt(c._id))) { vistas.add(idTxt(c._id)); lista.push(c); } };
  for (const o of datos?.opciones || []) cuentasDeOpcion(o).forEach(agregar);
  for (const co of datos?.cobros || []) { agregar(co.cuentaMT); agregar(co.par); }
  return lista;
};

export const buscarCuenta = (datos: any, id: string): any => todasLasCuentas(datos).find((c) => idTxt(c._id) === String(id));
export const buscarOpcion = (datos: any, id: string): any => (datos?.opciones || []).find((o: any) => idTxt(o._id) === String(id));

/** Opciones en el orden de la app (priority ascendente). */
export const opcionesPorPrioridad = (datos: any): any[] =>
  [...(datos?.opciones || [])].sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999) || String(a.title).localeCompare(String(b.title)));

// ------------------------------------------------------------------------------------------------
// Datos editables de una cuenta (ficha del núcleo <-> nombres del formulario)
// ------------------------------------------------------------------------------------------------

/** Los datos editables de una cuenta (ficha del GET) con los nombres que usa el núcleo al guardar. */
export const datosDeCuenta = (c: any): DatosCuenta => ({
  number: c?.numero ?? null, phone: c?.telefono ?? null, bankCode: c?.banco?.bankCode ?? null,
  taxId: c?.taxId ?? null, identity: c?.identity ?? null, name: c?.name ?? null, description: c?.description ?? null,
  subscriptor_simbolo: c?.subscriptor_simbolo ?? null, subscriptor_cuenta: c?.subscriptor_cuenta ?? null, subscriptor_mp: c?.subscriptor_mp ?? null
});

/** La cuenta con esos datos puestos (en la forma de la ficha). Solo toca los campos que vienen. */
const conDatos = (c: any, d: DatosCuenta): any => {
  const r: any = { ...c };
  if ('number' in d) r.numero = d.number ?? null;
  if ('phone' in d) r.telefono = d.phone ?? null;
  for (const k of ['taxId', 'identity', 'name', 'description', 'subscriptor_simbolo', 'subscriptor_cuenta', 'subscriptor_mp'] as (keyof DatosCuenta)[]) {
    if (k in d) r[k] = d[k] ?? null;
  }
  // El banco se identifica por su código (el catálogo no trae _id): se manda solo bankCode y el servidor deriva el banco
  if ('bankCode' in d) r.banco = { ...(c.banco || {}), _id: null, bankCode: d.bankCode ?? null, nombre: null };
  return r;
};

/** La cuenta guardada con los nombres de campo del borrador a la vista, para comparar campo por campo. */
const planaDe = (c: any): any => ({ ...c, ...datosDeCuenta(c), etiquetas: c?.etiquetas || [] });

/** Aplica a una cuenta los cambios del borrador (interruptores, etiquetas y datos). */
const conCambios = (c: any, cambios: CambiosCuenta): any => {
  const datos: any = {};
  const resto: any = {};
  for (const k of Object.keys(cambios)) ((CAMPOS_DATOS as string[]).includes(k) ? datos : resto)[k] = (cambios as any)[k];
  return conDatos({ ...c, ...resto }, datos);
};

/** La cuenta CT que sería la nueva, a partir de su MT. Nace apagada y oculta, como en el guion 01. */
export const cuentaNuevaDe = (mt: any, nueva: CuentaNueva): any => ({
  ...conDatos(mt, nueva.datos),
  _id: nueva.tmp_id, tmp_id: nueva.tmp_id, nueva: true, code: `CT nueva de ${mt.code}`, perfil: 'CT', equivale_a: mt.code,
  status: false, showClient: false, showChatbot: false, etiquetas: [], priority: null
});

// ------------------------------------------------------------------------------------------------
// Aplicar el borrador
// ------------------------------------------------------------------------------------------------

/** Los datos con el borrador puesto. No modifica `datos`. */
export const aplicarBorrador = (datos: any, borrador: Borrador): any => {
  if (!datos) return datos;
  const conCuenta = (c: any) => {
    const cambios = c && borrador.cuentas[idTxt(c._id)];
    return cambios ? conCambios(c, cambios) : c;
  };
  // Cuentas nuevas: cada una se une a su MT (en la opción, o en el cobro fijo si la MT es de un botón o del DI)
  const nuevas: { mt: any; cuenta: any }[] = [];
  for (const n of borrador.nuevas || []) {
    const mt = buscarCuenta(datos, n.clon_de);
    if (mt) nuevas.push({ mt, cuenta: cuentaNuevaDe(conCuenta(mt), n) });
  }
  const nuevaDe = (mt: any) => nuevas.find((x) => idTxt(x.mt._id) === idTxt(mt._id));
  const enCobro = (mt: any) => (datos.cobros || []).some((co: any) => co.cuentaMT && idTxt(co.cuentaMT._id) === idTxt(mt._id));
  return {
    ...datos,
    perfilCobra: borrador.perfilSimulado || datos.perfilCobra,
    opciones: (datos.opciones || []).map((o: any) => {
      const mts: any[] = (o.cuentas?.mt || []).map(conCuenta);
      const propias = (o.cuentas?.mt || []).map((mt: any) => nuevaDe(mt)).filter((x: any) => !!x && !enCobro(x.mt)).map((x: any) => x.cuenta);
      return { ...o, ...(borrador.opciones[idTxt(o._id)] || {}), cuentas: { mt: mts, ct: [...(o.cuentas?.ct || []).map(conCuenta), ...propias] } };
    }),
    cobros: (datos.cobros || []).map((co: any) => {
      const par = co.par ? conCuenta(co.par) : (co.cuentaMT && nuevaDe(co.cuentaMT)?.cuenta) || co.par;
      return { ...co, cuentaMT: conCuenta(co.cuentaMT), par };
    })
  };
};

/** El estado que entienden las reglas, igual que `paraReglas` del núcleo. */
export const estadoParaReglas = (datos: any): any => ({
  perfilCobra: datos?.perfilCobra || null,
  opciones: (datos?.opciones || []).map((o: any) => ({ _id: idTxt(o._id), code: o.code, title: o.title, showClient: o.showClient, priority: o.priority })),
  cuentas: todasLasCuentas(datos).map((c) => ({
    _id: idTxt(c._id), code: c.code, payment_option: c.payment_option === null || c.payment_option === undefined ? null : idTxt(c.payment_option),
    payment_method: c.metodo?.code ?? null, bank: c.banco?.bankCode ?? c.banco?._id ?? null, perfil: c.perfil || null, equivale_a: c.equivale_a || null,
    status: c.status, showClient: c.showClient, showChatbot: c.showChatbot, typeConfirm: c.typeConfirm
  })),
  cobros: (datos?.cobros || []).map((co: any) => ({
    servicio: co.servicio, opcion: co.opcion === null || co.opcion === undefined ? null : idTxt(co.opcion),
    cuentaMT: co.cuentaMT ? idTxt(co.cuentaMT._id) : null, cuentaCT: co.par ? idTxt(co.par._id) : null
  }))
});

/** Bloqueos del borrador (las mismas reglas que el servidor). */
export const bloqueosDelBorrador = (datos: any, borrador: Borrador): any[] => revisar(estadoParaReglas(aplicarBorrador(datos, borrador)));

/** Botón o DI sin cuenta activa del contrato que cobra (regla 14): se avisa, no impide guardar. */
export const avisosDelBorrador = (datos: any, borrador: Borrador): any[] => avisar(estadoParaReglas(aplicarBorrador(datos, borrador)));

/** Cuentas de MT visibles sin par: frenan el switch, pero no el guardado. */
export const frenosDelBorrador = (datos: any, borrador: Borrador): any[] =>
  revisar(estadoParaReglas(aplicarBorrador(datos, borrador)), { switch: true }).filter((b) => b.codigo === 'mt_sin_par');

// ------------------------------------------------------------------------------------------------
// Editar el borrador (cada función devuelve un borrador nuevo)
// ------------------------------------------------------------------------------------------------

/** Deja en el borrador solo lo que difiere de lo guardado. */
const limpiarPorId = (guardado: any, nuevo: any, campos: string[]): any | null => {
  const dif: any = {};
  for (const k of campos) if (nuevo[k] !== undefined && !igual(guardado[k], nuevo[k])) dif[k] = nuevo[k];
  return Object.keys(dif).length ? dif : null;
};

export const ponerOpcion = (datos: any, b: Borrador, id: string, cambio: CambiosOpcion): Borrador => {
  const guardada = buscarOpcion(datos, id);
  if (!guardada) return b;
  const r = copiaBorrador(b);
  const dif = limpiarPorId(guardada, { ...(b.opciones[id] || {}), ...cambio }, CAMPOS_OPCION as string[]);
  if (dif) r.opciones[id] = dif; else delete r.opciones[id];
  return r;
};

export const ponerCuenta = (datos: any, b: Borrador, id: string, cambio: CambiosCuenta): Borrador => {
  const guardada = buscarCuenta(datos, id);
  if (!guardada) return b;
  const r = copiaBorrador(b);
  const base = { ...(b.cuentas[id] || {}), ...cambio };
  const dif: any = limpiarPorId(planaDe(guardada), base, CAMPOS_CUENTA as string[]);
  if (dif) r.cuentas[id] = dif; else delete r.cuentas[id];
  return r;
};

/** Edita los datos de una cuenta CT (nunca de una MT, que cobra en producción). El borrador guarda solo lo que cambió. */
export const editarCuenta = (datos: any, b: Borrador, id: string, d: DatosCuenta): Borrador => {
  const guardada = buscarCuenta(datos, id);
  if (!guardada || perfilDe(guardada) === 'MT') return b;
  const solo: DatosCuenta = {};
  for (const k of CAMPOS_DATOS) if (k in d) (solo as any)[k] = d[k];
  return ponerCuenta(datos, b, id, solo);
};

/**
 * Agrega al borrador una cuenta CT nueva, clon de una MT. Solo una MT sin par CT (ni guardado ni ya clonado en el borrador).
 * `d` es el formulario completo; `tmpId` lo pone quien llama (único en el borrador).
 */
export const clonarComoCT = (datos: any, b: Borrador, mtId: string, tmpId: string, d: DatosCuenta): Borrador => {
  const mt = buscarCuenta(aplicarBorrador(datos, b), mtId);
  if (!mt || perfilDe(mt) !== 'MT' || parDe(aplicarBorrador(datos, b), mt)) return b;
  const r = copiaBorrador(b);
  r.nuevas.push({ tmp_id: tmpId, clon_de: idTxt(mt._id), datos: { ...datosDeCuenta(mt), ...d } });
  return r;
};

/** Cambia los datos de una cuenta nueva del borrador. */
export const editarNueva = (b: Borrador, tmpId: string, d: DatosCuenta): Borrador => {
  const r = copiaBorrador(b);
  r.nuevas = r.nuevas.map((n) => (n.tmp_id === tmpId ? { ...n, datos: { ...n.datos, ...d } } : n));
  return r;
};

/** Quita una cuenta nueva del borrador (antes de guardar). */
export const quitarNueva = (b: Borrador, tmpId: string): Borrador => {
  const r = copiaBorrador(b);
  r.nuevas = r.nuevas.filter((n) => n.tmp_id !== tmpId);
  return r;
};

/** De la MT, solo lo que difiere en los datos de la nueva (lo que se manda al núcleo). */
export const diferenciasConLaMT = (mt: any, d: DatosCuenta): DatosCuenta => {
  const base: any = datosDeCuenta(mt);
  const dif: any = {};
  for (const k of CAMPOS_DATOS) if (k in d && !igual(base[k], d[k])) dif[k] = d[k];
  return dif;
};

/** Etiquetas de una cuenta con el borrador. */
export const etiquetasDe = (datos: any, b: Borrador, id: string): string[] => {
  const cambio = b.cuentas[id]?.etiquetas;
  return cambio !== undefined ? cambio : (buscarCuenta(datos, id)?.etiquetas || []);
};

const claveEtiqueta = (n: string) => String(n || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

export const agregarEtiqueta = (datos: any, b: Borrador, id: string, nombre: string): Borrador => {
  const actuales = etiquetasDe(datos, b, id);
  if (actuales.some((e) => claveEtiqueta(e) === claveEtiqueta(nombre))) return b;
  return ponerCuenta(datos, b, id, { etiquetas: [...actuales, nombre] });
};

export const quitarEtiqueta = (datos: any, b: Borrador, id: string, nombre: string): Borrador =>
  ponerCuenta(datos, b, id, { etiquetas: etiquetasDe(datos, b, id).filter((e) => claveEtiqueta(e) !== claveEtiqueta(nombre)) });

/** Sube o baja una opción un lugar: intercambia su priority con la vecina, como el reorden de hoy (1..total sin repetir). */
export const moverOpcion = (datos: any, b: Borrador, id: string, direccion: 'subir' | 'bajar'): Borrador => {
  const efectivo = aplicarBorrador(datos, b);
  const orden = opcionesPorPrioridad(efectivo);
  const i = orden.findIndex((o) => idTxt(o._id) === String(id));
  const j = direccion === 'subir' ? i - 1 : i + 1;
  if (i < 0 || j < 0 || j >= orden.length) return b;
  const pi = orden[i].priority, pj = orden[j].priority;
  let r = ponerOpcion(datos, b, idTxt(orden[i]._id), { priority: pj });
  r = ponerOpcion(datos, r, idTxt(orden[j]._id), { priority: pi });
  return r;
};

export const hayCambios = (b: Borrador): boolean =>
  !!(Object.keys(b.opciones).length || Object.keys(b.cuentas).length || (b.nuevas || []).length || b.perfilSimulado);

// ------------------------------------------------------------------------------------------------
// Pares MT → CT
// ------------------------------------------------------------------------------------------------

export interface ParCuentas { mt: any | null; ct: any | null; }

/** Empareja MT con CT por `equivale_a` = código de la MT y perfil distinto de MT, igual que el núcleo. Las CT sin par van solas. */
export const paresDeOpcion = (opcion: any): ParCuentas[] => {
  const mts: any[] = opcion?.cuentas?.mt || [];
  const cts: any[] = opcion?.cuentas?.ct || [];
  const usadas = new Set<string>();
  const pares: ParCuentas[] = mts.map((mt) => {
    const ct = cts.find((c) => c.equivale_a && c.equivale_a === mt.code) || null;
    if (ct) usadas.add(idTxt(ct._id));
    return { mt, ct };
  });
  for (const ct of cts) if (!usadas.has(idTxt(ct._id))) pares.push({ mt: null, ct });
  return pares;
};

/** Cuenta de otro contrato que reemplaza a esta, o la MT que ella reemplaza. */
export const parDe = (datos: any, cuenta: any): any => {
  const todas = todasLasCuentas(datos);
  if (perfilDe(cuenta) === 'MT') return todas.find((c) => perfilDe(c) !== 'MT' && c.equivale_a === cuenta.code) || null;
  return todas.find((c) => perfilDe(c) === 'MT' && c.code === cuenta.equivale_a) || null;
};

// ------------------------------------------------------------------------------------------------
// Preparar el corte del 1-nov
// ------------------------------------------------------------------------------------------------

/**
 * Llena el borrador con el corte: CT como el que cobra y, en pago móvil y transferencia, la cuenta CT de cada par visible
 * toma la visibilidad de su MT (y se enciende) y la MT queda oculta. Es la misma regla que aplica el corte del switch.
 * No guarda nada. Parte de lo guardado: descarta otros cambios sin guardar de las cuentas.
 */
export const prepararCorte = (datos: any, perfilDestino = 'CT'): Borrador => {
  let b = borradorVacio();
  const elegibles = (datos?.opciones || []).flatMap((o: any) => cuentasDeOpcion(o));
  for (const mt of elegibles) {
    if (perfilDe(mt) !== 'MT' || !METODOS_PAGO_MOVIL_Y_TRANSFERENCIA.includes(mt.metodo?.code)) continue;
    if (!(mt.showClient === true || mt.showChatbot === true)) continue;
    const par = elegibles.find((c: any) => perfilDe(c) === perfilDestino && c.equivale_a === mt.code);
    if (!par) continue;
    b = ponerCuenta(datos, b, idTxt(par._id), { status: true, showClient: !!mt.showClient, showChatbot: !!mt.showChatbot });
    b = ponerCuenta(datos, b, idTxt(mt._id), { showClient: false, showChatbot: false });
  }
  return { ...b, perfilSimulado: perfilDestino };
};

// ------------------------------------------------------------------------------------------------
// Cambios contra lo guardado
// ------------------------------------------------------------------------------------------------

/** Lo que se manda a POST /cuentas-contrato/guardar: solo lo que cambia. */
export const payloadDeCambios = (datos: any, b: Borrador): { opciones: any[]; cuentas: any[]; nuevas?: any[] } => {
  const opciones: any[] = [];
  for (const o of datos?.opciones || []) {
    const c = b.opciones[idTxt(o._id)];
    const dif = c ? limpiarPorId(o, c, CAMPOS_OPCION as string[]) : null;
    if (dif) opciones.push({ _id: idTxt(o._id), ...dif });
  }
  const cuentas: any[] = [];
  for (const cuenta of todasLasCuentas(datos)) {
    const c = b.cuentas[idTxt(cuenta._id)];
    const dif = c ? limpiarPorId(planaDe(cuenta), c, CAMPOS_CUENTA as string[]) : null;
    if (dif) cuentas.push({ _id: idTxt(cuenta._id), ...dif });
  }
  // `nuevas` solo viaja si hay: sin cuentas nuevas el body es el de siempre
  const nuevas: any[] = [];
  for (const n of b.nuevas || []) {
    const mt = buscarCuenta(datos, n.clon_de);
    if (mt) nuevas.push({ tmp_id: n.tmp_id, clon_de: idTxt(mt._id), datos: diferenciasConLaMT(mt, n.datos) });
  }
  return nuevas.length ? { opciones, cuentas, nuevas } : { opciones, cuentas };
};

/** Cuántos cambios reales hay (cada opción o cuenta cuenta una vez por campo). */
export const contarCambios = (datos: any, b: Borrador): number => {
  const p = payloadDeCambios(datos, b);
  const contar = (lista: any[]) => lista.reduce((n, x) => n + Object.keys(x).length - 1, 0);
  return contar(p.opciones) + contar(p.cuentas) + (p.nuevas || []).length;
};

/** ¿Esta opción o alguna de sus cuentas tiene cambios sin guardar? */
export const opcionTieneCambios = (datos: any, b: Borrador, opcion: any): boolean =>
  !!b.opciones[idTxt(opcion._id)] || cuentasDeOpcion(opcion).some((c) => !!b.cuentas[idTxt(c._id)] || !!c.nueva);

/** Nombre de una cuenta para decirla en palabras: «Pago móvil · Banco de Venezuela (MT)». */
export const nombreCuenta = (c: any, bancos?: { [codigo: string]: string }): string => {
  if (!c) return '';
  const metodo = c.metodo?.name || c.metodo?.code || 'Cuenta';
  const banco = c.banco?.nombre || (c.banco?.bankCode && bancos ? bancos[c.banco.bankCode] : '') || c.banco?.bankCode || '';
  const dato = c.telefono && c.metodo?.code === 'PAGO_MOVIL' ? ` ${c.telefono}` : '';
  return `${metodo}${banco ? ' · ' + banco : ''}${dato} (${perfilDe(c)})`.trim();
};

const valorTxt = (v: any): string => (v === null || v === undefined || v === '' ? 'vacío' : `«${v}»`);
const nombreBancoDe = (codigo: any, nombre: any, bancos?: { [codigo: string]: string }): string =>
  nombre || (codigo && bancos && bancos[codigo]) || codigo || '';

export interface LineaCambio { texto: string; tipo: 'opcion' | 'cuenta' | 'orden' | 'switch'; opcion?: string; cuenta?: string; }

/** Lista legible de cambios: visibilidad y orden de las opciones, mostrar u ocultar, encender o apagar, chatbot y etiquetas. */
export const listaDeCambios = (datos: any, b: Borrador, bancos?: { [codigo: string]: string }): LineaCambio[] => {
  const lineas: LineaCambio[] = [];
  const efectivo = aplicarBorrador(datos, b);
  for (const o of opcionesPorPrioridad(datos)) {
    const c = b.opciones[idTxt(o._id)];
    if (c && c.showClient !== undefined && c.showClient !== o.showClient) {
      lineas.push({ tipo: 'opcion', opcion: idTxt(o._id), texto: `${c.showClient ? 'Se muestra' : 'Se oculta'} en la app la opción «${o.title}».` });
    }
  }
  const antes = opcionesPorPrioridad(datos).map((o) => idTxt(o._id)).join(',');
  const despues = opcionesPorPrioridad(efectivo).map((o) => idTxt(o._id)).join(',');
  if (antes !== despues) {
    lineas.push({ tipo: 'orden', texto: `Nuevo orden en la app: ${opcionesPorPrioridad(efectivo).map((o) => o.title).join(' → ')}.` });
  }
  const opcionDe = (c: any) => (c.payment_option === null || c.payment_option === undefined ? undefined : idTxt(c.payment_option));
  for (const cuenta of todasLasCuentas(datos)) {
    const c = b.cuentas[idTxt(cuenta._id)];
    if (!c) continue;
    const nombre = nombreCuenta(cuenta, bancos);
    const base = { tipo: 'cuenta' as const, cuenta: idTxt(cuenta._id), opcion: opcionDe(cuenta) };
    if (c.showClient !== undefined && c.showClient !== cuenta.showClient) lineas.push({ ...base, texto: `${c.showClient ? 'Se muestra al cliente' : 'Se oculta al cliente'}: ${nombre}.` });
    if (c.status !== undefined && c.status !== cuenta.status) lineas.push({ ...base, texto: `${c.status ? 'Se enciende' : 'Se apaga'}: ${nombre}.` });
    if (c.showChatbot !== undefined && c.showChatbot !== cuenta.showChatbot) lineas.push({ ...base, texto: `${c.showChatbot ? 'Se muestra en el chatbot' : 'Se oculta en el chatbot'}: ${nombre}.` });
    // Datos editados de una cuenta CT: campo, antes → después
    const plana = planaDe(cuenta);
    for (const k of CAMPOS_DATOS) {
      if (c[k] === undefined || igual(plana[k], c[k])) continue;
      if (k === 'bankCode') {
        const nuevo = c.bankCode ?? null;
        lineas.push({ ...base, texto: `Cambia el banco de ${nombre}: ${nombreBancoDe(cuenta.banco?.bankCode, cuenta.banco?.nombre, bancos) || 'sin banco'} → ${nombreBancoDe(nuevo, null, bancos) || 'sin banco'}.` });
        continue;
      }
      lineas.push({ ...base, texto: `Cambia ${enFrase(ETIQUETA_CAMPO[k])} de ${nombre}: ${valorTxt(plana[k])} → ${valorTxt(c[k])}.` });
    }
    if (c.etiquetas !== undefined) {
      const antesE: string[] = cuenta.etiquetas || [];
      const ponen = c.etiquetas.filter((e) => !antesE.some((x) => claveEtiqueta(x) === claveEtiqueta(e)));
      const quitan = antesE.filter((e) => !c.etiquetas!.some((x) => claveEtiqueta(x) === claveEtiqueta(e)));
      for (const e of ponen) lineas.push({ ...base, texto: `Etiqueta «${e}» puesta en: ${nombre}.` });
      for (const e of quitan) lineas.push({ ...base, texto: `Etiqueta «${e}» quitada de: ${nombre}.` });
    }
  }
  for (const n of b.nuevas || []) {
    const mt = buscarCuenta(datos, n.clon_de);
    if (!mt) continue;
    const dif: any = diferenciasConLaMT(mt, n.datos);
    const detalle = CAMPOS_DATOS.filter((k) => k in dif).map((k) => {
      if (k === 'bankCode') return `banco ${nombreBancoDe(n.datos.bankCode, null, bancos) || 'sin banco'}`;
      return `${enFrase(ETIQUETA_CAMPO[k])} ${valorTxt(dif[k])}`;
    });
    lineas.push({
      tipo: 'cuenta', cuenta: n.tmp_id, opcion: mt.payment_option === null || mt.payment_option === undefined ? undefined : idTxt(mt.payment_option),
      texto: `Cuenta CT nueva (clon de ${mt.code})${detalle.length ? ': ' + detalle.join(', ') : ''}. Nace apagada y oculta.`
    });
  }
  if (b.perfilSimulado && b.perfilSimulado !== datos?.perfilCobra) {
    lineas.push({ tipo: 'switch', texto: `Con ${nombreContrato(b.perfilSimulado)} cobrando (esto no se guarda aquí: el contrato cambia con «Pasar a ${b.perfilSimulado}» en Contrato activo).` });
  }
  return lineas;
};

/** ¿El borrador simula otro contrato? Entonces no se puede guardar desde aquí: el servidor lo rechazaría. */
export const simulaOtroContrato = (datos: any, b: Borrador): boolean => !!b.perfilSimulado && b.perfilSimulado !== datos?.perfilCobra;

// ------------------------------------------------------------------------------------------------
// Bitácora: un cambio guardado, en palabras
// ------------------------------------------------------------------------------------------------

const textoCampo = (campo: string, valor: any): string => {
  if (campo === 'showClient') return valor ? 'se muestra al cliente' : 'se oculta al cliente';
  if (campo === 'showChatbot') return valor ? 'se muestra en el chatbot' : 'se oculta en el chatbot';
  if (campo === 'status') return valor ? 'se enciende' : 'se apaga';
  if (campo === 'priority') return `pasa al lugar ${valor}`;
  if (campo === 'etiquetas') return `etiquetas: ${(valor || []).length ? (valor as string[]).join(', ') : 'ninguna'}`;
  if (ETIQUETA_CAMPO[campo]) return `${enFrase(ETIQUETA_CAMPO[campo])} ${valorTxt(valor)}`;
  return `${campo}: ${JSON.stringify(valor)}`;
};

/** ¿Ese cambio de la bitácora creó alguna cuenta? */
export const hayCuentaCreada = (cambio: any): boolean => (cambio?.cambios || []).some((e: any) => !!e?.creada);

/** Cada entrada de la bitácora ({tipo, code, antes, despues}) en una frase. */
export const frasesDeBitacora = (cambio: any): string[] =>
  (cambio?.cambios || []).map((e: any) => {
    if (e.creada) {
      const clon = e.despues?.equivale_a ? ` (clon de ${e.despues.equivale_a})` : '';
      return `Cuenta CT creada${clon}: ${e.code}. Nació apagada y oculta; al volver atrás no se borra, se apaga y se oculta.`;
    }
    const que = e.tipo === 'opcion' ? `Opción ${e.code}` : `Cuenta ${e.code}`;
    const partes = Object.keys(e.despues || {}).map((k) => {
      // Un dato editado se dice «campo: antes → después»
      if (ETIQUETA_CAMPO[k] && e.antes && k in e.antes) return `${enFrase(ETIQUETA_CAMPO[k])}: ${valorTxt(e.antes[k])} → ${valorTxt(e.despues[k])}`;
      return textoCampo(k, e.despues[k]);
    });
    return `${que}: ${partes.join('; ')}.`;
  });

// ------------------------------------------------------------------------------------------------
// Lo que ve el cliente (el teléfono)
// ------------------------------------------------------------------------------------------------

export const nombreServicio = (servicio: string): string => {
  const nombres: any = {
    'boton-bdv-tdd': 'TDD', 'boton-bdv-tdc': 'TDC', 'boton-mercantil-tdd': 'TDD', 'boton-mercantil-tdc': 'TDC',
    'boton-mercantil-tdc-5434': 'TDC 5434', 'debito-inmediato': 'DI', 'debito-inmediato-atascado': 'DI (recuperación)'
  };
  return nombres[servicio] || servicio;
};

export interface ItemTelefono { _id: string; banco: string; dato: string; titular: string; marca?: 'entra' | 'sale'; }
export interface ParteTelefono { metodo: string; items: ItemTelefono[]; }
export interface OpcionTelefono {
  _id: string; titulo: string; texto: string; marca?: 'entra' | 'sale';
  partes: ParteTelefono[];
  cobra?: string;           // «Cobra CrediTotal · TDD · TDC»
  fallan?: string[];        // cobros fijos sin cuenta del contrato que cobra
  vacia: boolean;           // se abriría sin ninguna cuenta
}
export interface VistaTelefono { opciones: OpcionTelefono[]; reordenada: boolean; perfil: string | null; }

const fijasDe = (datos: any): Set<string> => {
  const f = new Set<string>();
  for (const co of datos?.cobros || []) { if (co.cuentaMT) f.add(idTxt(co.cuentaMT._id)); if (co.par) f.add(idTxt(co.par._id)); }
  return f;
};

const itemDe = (c: any, bancos?: { [codigo: string]: string }): ItemTelefono => {
  const banco = c.banco?.nombre || (c.banco?.bankCode && bancos ? bancos[c.banco.bankCode] : '') || c.banco?.bankCode || (c.metodo?.name || 'Cuenta');
  const dato = c.metodo?.code === 'PAGO_MOVIL' ? [c.telefono, c.taxId].filter(Boolean).join(' · ') : (c.numero || '');
  return { _id: idTxt(c._id), banco, dato, titular: `${nombreContrato(perfilDe(c))}${c.taxId && c.metodo?.code !== 'PAGO_MOVIL' ? ' · ' + c.taxId : ''}` };
};

/** Lo que vería el cliente con un estado de datos (ya con el borrador puesto, si hace falta). */
export const vistaCliente = (datos: any, bancos?: { [codigo: string]: string }): OpcionTelefono[] => {
  const fijas = fijasDe(datos);
  const perfil = datos?.perfilCobra ? String(datos.perfilCobra).toUpperCase() : null;
  return opcionesPorPrioridad(datos).filter((o) => o.showClient === true).map((o) => {
    const visibles = cuentasDeOpcion(o).filter((c) => !fijas.has(idTxt(c._id)) && esVisible(c));
    const porMetodo = new Map<string, ItemTelefono[]>();
    for (const c of visibles) {
      const m = c.metodo?.name || c.metodo?.code || 'Otros';
      if (!porMetodo.has(m)) porMetodo.set(m, []);
      (porMetodo.get(m) as ItemTelefono[]).push(itemDe(c, bancos));
    }
    const partes: ParteTelefono[] = [];
    porMetodo.forEach((items, metodo) => partes.push({ metodo, items }));
    const cobros = (datos.cobros || []).filter((co: any) => co.opcion !== null && co.opcion !== undefined && idTxt(co.opcion) === idTxt(o._id));
    let cobra: string | undefined; let fallan: string[] | undefined;
    if (cobros.length) {
      const cobraMT = perfil === 'MT';
      const sin = cobros.filter((co: any) => { const c = cobraMT ? co.cuentaMT : co.par; return !c || c.status === false; });
      fallan = sin.map((co: any) => nombreServicio(co.servicio));
      cobra = `Cobra ${perfil ? nombreContrato(perfil) : 'un contrato sin identificar'}${cobros.length > 1 ? ' · ' + cobros.map((co: any) => nombreServicio(co.servicio)).join(' · ') : ''}`;
    }
    const tieneElegibles = cuentasDeOpcion(o).some((c) => !fijas.has(idTxt(c._id)));
    return { _id: idTxt(o._id), titulo: o.title, texto: o.description || '', partes, cobra, fallan: fallan && fallan.length ? fallan : undefined, vacia: tieneElegibles && !visibles.length };
  });
};

/** El teléfono: lo guardado (Hoy) o el borrador, este último marcado con lo que entra y lo que sale. */
export const vistaTelefono = (datos: any, b: Borrador, modo: 'hoy' | 'borrador', bancos?: { [codigo: string]: string }): VistaTelefono => {
  const hoy = vistaCliente(datos, bancos);
  if (modo === 'hoy') return { opciones: hoy, reordenada: false, perfil: datos?.perfilCobra || null };
  const efectivo = aplicarBorrador(datos, b);
  const ahora = vistaCliente(efectivo, bancos);
  const idsHoy = hoy.map((o) => o._id), idsAhora = ahora.map((o) => o._id);
  const opciones: OpcionTelefono[] = ahora.map((o) => {
    const previa = hoy.find((x) => x._id === o._id);
    const partes = o.partes.map((p) => {
      const prev = previa?.partes.find((x) => x.metodo === p.metodo)?.items.map((i) => i._id) || [];
      const idsP = p.items.map((i) => i._id);
      const salen = (previa?.partes.find((x) => x.metodo === p.metodo)?.items || []).filter((i) => !idsP.includes(i._id)).map((i) => ({ ...i, marca: 'sale' as const }));
      return { ...p, items: [...p.items.map((i) => (previa && !prev.includes(i._id) ? { ...i, marca: 'entra' as const } : i)), ...salen] };
    });
    // Métodos que tenía hoy y ya no tiene nada visible: sus cuentas «salen»
    for (const p of previa?.partes || []) {
      if (!o.partes.some((x) => x.metodo === p.metodo)) partes.push({ metodo: p.metodo, items: p.items.map((i) => ({ ...i, marca: 'sale' as const })) });
    }
    return { ...o, partes, ...(previa ? {} : { marca: 'entra' as const }) };
  });
  for (const o of hoy) if (!idsAhora.includes(o._id)) opciones.push({ ...o, marca: 'sale', partes: [], cobra: undefined, fallan: undefined, vacia: false });
  const reordenada = idsHoy.filter((i) => idsAhora.includes(i)).join() !== idsAhora.filter((i) => idsHoy.includes(i)).join();
  return { opciones, reordenada, perfil: efectivo?.perfilCobra || null };
};

// ------------------------------------------------------------------------------------------------
// Filtro por etiqueta
// ------------------------------------------------------------------------------------------------

export const cuentaTieneEtiqueta = (c: any, nombre: string): boolean =>
  (c?.etiquetas || []).some((e: string) => claveEtiqueta(e) === claveEtiqueta(nombre));

/** Cuántas cuentas lleva cada etiqueta (con el borrador). */
export const conteoDeEtiquetas = (datos: any): { [nombre: string]: number } => {
  const r: { [nombre: string]: number } = {};
  for (const c of todasLasCuentas(datos)) for (const e of c.etiquetas || []) r[e] = (r[e] || 0) + 1;
  return r;
};

/** Pares de una opción que llevan la etiqueta en alguna de sus dos cuentas. */
export const paresConEtiqueta = (opcion: any, nombre: string): ParCuentas[] =>
  paresDeOpcion(opcion).filter((p) => cuentaTieneEtiqueta(p.mt, nombre) || cuentaTieneEtiqueta(p.ct, nombre));
